import { useMemo, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import countryTelephoneData from 'country-telephone-data'
import XLSX from 'xlsx'
import provincesFile from './stateOrProvincecode.xlsx'
import { ReactComponent as WarningCircleFillIcon } from '../../../assets/icons/warning-circle-fill.svg'
import {
  Box,
  Button,
  FormHelperText,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'

import useStyles from './styles'
import useTranslations from '../../../i18n/useTranslations'
import Autocomplete from '../../../ui/components/autocomplete'
import StyledPaper from '../../../ui/components/StyledPaper'
import { useDestinationCountries } from '../../../services/destinationCountries'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import GooglePlacesAutocomplete from '../../../context/ShippingSend/Step1Origin/NewAddress/GooglePlacesAutocomplete'
import Select from '../../../ui/components/select'
import calculateProvinceAndLocalities, {
  GEOCODING_ERRORS,
} from '../../../context/ShippingSend/calculateProvinceAndLocalities'
import useDebounce from '../../../context/ShippingSend/useDebounce'
import { DEBOUNCED_POSTAL_CODE_DELAY } from '../../../context/ShippingSend/Step1Origin'
import {
  useAddAddressMutation,
  useEditAddressAgendaMutation,
} from '../../../services/addressBook'
import TooltipButton from '../../../ui/components/tooltipButton'

const propTypes = {
  clientCenterCode: PropTypes.string,
  onSuccessAddContact: PropTypes.func,
  onErrorAddContact: PropTypes.func,
  onSuccessEditContact: PropTypes.func,
  onErrorEditContact: PropTypes.func,
  addEditAddress: PropTypes.any,
  onCancel: PropTypes.func,
}

const getAddressSchema = () => {
  return Yup.object().shape({
    address: Yup.object().shape({
      country: Yup.object().shape({
        label: Yup.string().required(),
        code: Yup.string().required(),
      }),
      contactClientCode: Yup.string(),
      address: Yup.string().required(),
      name: Yup.string().required(),
      phonePrefix: Yup.string(),
      phone: Yup.string()
        .required()
        .test(
          'isValidPhoneNumber',
          {
            key: 'shipping-send-form:new-address:phone-format-or-length-error',
          },
          function (value, schema) {
            return (
              value?.trim()?.length > 0 && value?.length < 16 && !isNaN(Number(value))
            )
            // const phonePrefix = schema.parent.phonePrefix ?? '+34'
            // const fullPhoneNumber = `${phonePrefix}${value}`
            // return isValidPhoneNumber(fullPhoneNumber)
          },
        ),
      email: Yup.string().email(),
      stair: Yup.string(),
      floor: Yup.string(),
      letter: Yup.string(),
      postalCode: Yup.string().when('country.code', {
        is: 'ES',
        then: Yup.string()
          .length(5, {
            key: 'shipping-send-form:origin:postal-code:invalid-format',
          })
          .required({
            key: 'error:field:postal-code:required',
          }),
        otherwise: Yup.string().when('country.code', {
          is: 'PT',
          then: Yup.string()
            .matches(/^(?:\d{4}|\d{4}-\d{3})$/, {
              key: 'shipping-send-form:origin:postal-code:invalid-format',
            })
            .required({
              key: 'error:field:postal-code:required',
            }),
          otherwise: Yup.string().required({
            key: 'error:field:postal-code:required',
          }),
        }),
      }),
      province: Yup.string().required(),
      city: Yup.string().required(),
    }),

    comments: Yup.string(),
  })
}

const AddEditDirection = ({
  clientCenterCode,
  onSuccessAddContact,
  onErrorAddContact,
  onSuccessEditContact,
  onErrorEditContact,
  addEditAddress,
  onCancel,
}) => {
  const classes = useStyles()
  const formatMessage = useTranslations()
  const { data: countries } = useDestinationCountries()
  const [fromSearch, setFromSearch] = useState()
  const [provicesByCountryCode, setProvincesByCountryCode] = useState([])
  const [initialLoad, setInitialLoad] = useState(true)
  const [firstRender, setFirstRender] = useState(true)
  const [zeroResultsGeocode, setZeroResultsGeocode] = useState(false)

  const getPhoneInfo = phoneSource => {
    let phone = phoneSource?.replace(/\s/g, '')
    let prefix = '+34'

    if (phone?.length && phone[0] === '+') {
      let findPrefix = countryTelephoneData.allCountries.reduce(
        (longestMatch, { dialCode }) => {
          if (
            phone.startsWith('+' + dialCode) &&
            dialCode.length > longestMatch.length
          ) {
            return dialCode
          }
          return longestMatch
        },
        '',
      )

      phone = phone.replace('+' + findPrefix, '')
      prefix = '+' + findPrefix
    }
    return { prefix: prefix, phone: phone }
  }

  const extractAddressDetails = address => {
    if (!address) return null
    const details = {
      stair: null,
      floor: null,
      letter: null,
    }

    // Regex patterns for stair, floor, and letter
    const stairPattern = /escalera\s+(\d+)/i
    const floorPattern = /piso\s+(\d+)/i
    const letterPattern = /letra\s+(\w+)/i

    // Extracting the details using regex
    const stairMatch = address.match(stairPattern)
    if (stairMatch) {
      details.stair = stairMatch[1]
    }

    const floorMatch = address.match(floorPattern)
    if (floorMatch) {
      details.floor = floorMatch[1]
    }

    const letterMatch = address.match(letterPattern)
    if (letterMatch) {
      details.letter = letterMatch[1]
    }

    return details
  }

  const removeStairFloorLetter = address => {
    if (!address) return null

    // Regex patterns to match stair, floor, and letter
    const stairPattern = /,\s*escalera\s+\d+/i
    const floorPattern = /,\s*piso\s+\d+/i
    const letterPattern = /,\s*letra\s+\w+/i

    // Remove stair, floor, and letter from the address
    let cleanedAddress = address.replace(stairPattern, '')
    cleanedAddress = cleanedAddress.replace(floorPattern, '')
    cleanedAddress = cleanedAddress.replace(letterPattern, '')

    // Trim any extra whitespace or commas
    cleanedAddress = cleanedAddress.replace(/\s*,\s*/g, ', ').trim()

    return cleanedAddress
  }

  const formatAddress = details => {
    let { address, stair, floor, letter } = details

    if (stair) {
      address += `, escalera ${stair}`
    }

    if (floor) {
      address += `, piso ${floor}`
    }

    if (letter) {
      address += `, letra ${letter}`
    }

    return address
  }

  const AddAddressMutation = useAddAddressMutation(
    onSuccessAddContact,
    onErrorAddContact,
  )

  const EditAddressMutation = useEditAddressAgendaMutation(
    onSuccessEditContact,
    onErrorEditContact,
  )

  const formik = useFormik({
    validateOnChange: false,
    validationSchema: getAddressSchema(),
    initialValues: {
      address: {
        country: countries?.find(
          ({ code }) => code === addEditAddress?.country,
        ) ?? { code: 'ES', label: 'España' },
        name: addEditAddress?.name ?? '',
        address: removeStairFloorLetter(addEditAddress?.address) ?? '',
        phone: getPhoneInfo(addEditAddress?.phone)?.phone,
        phonePrefix: getPhoneInfo(addEditAddress?.phone)?.prefix,
        email: addEditAddress?.email ?? '',
        stair:
          addEditAddress?.stair ??
          extractAddressDetails(addEditAddress?.address)?.stair ??
          '',
        floor:
          addEditAddress?.floor ??
          extractAddressDetails(addEditAddress?.address)?.floor ??
          '',
        letter:
          addEditAddress?.letter ??
          extractAddressDetails(addEditAddress?.address)?.letter ??
          '',
        postalCode: addEditAddress?.postalCode ?? '',
        province: addEditAddress?.province ?? '',
        city: addEditAddress?.city ?? '',
        hasSeveralLocalities: false,
        localitiesOptions: null,
        contactClientCode: addEditAddress?.contactClientCode ?? '',
      },
      comments: addEditAddress?.comments ?? '',
    },
    onSubmit: values => {
      const modifiedValues = JSON.parse(JSON.stringify(values))
      modifiedValues.address.phone =
        values?.address.phonePrefix + values?.address.phone?.replace(/\s/g, '')
      modifiedValues.address.address = formatAddress(modifiedValues.address)
      if (addEditAddress?.id)
        EditAddressMutation.mutate({
          addressData: modifiedValues,
          clientCenterCode: clientCenterCode,
          clientAddressCode: addEditAddress?.id,
        })
      else {
        AddAddressMutation.mutate({
          addressData: modifiedValues,
          clientCenterCode: clientCenterCode,
        })
      }
    },
  })
  const countriesToSuggestAddresses = useMemo(() => {
    const selectedCountry = formik?.values?.address?.country?.code
    return selectedCountry
      ? [selectedCountry]
      : Object.values({
          Spain: 'ES',
          Portugal: 'PT',
        })
  }, [formik?.values?.address?.country?.code])

  const debouncedPostalCode = useDebounce(
    formik.values.address.postalCode,
    DEBOUNCED_POSTAL_CODE_DELAY,
  )

  useEffect(() => {
    if (
      !initialLoad &&
      debouncedPostalCode.trim() !== '' &&
      formik.values.address.country?.code !== '' &&
      formik.values.address.country?.code !== undefined
    ) {
      calculateProvinceAndLocalities(
        debouncedPostalCode,
        formik.values.address.country.code,
        fromSearch,
      )
        .then(result => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              ...result,
            },
          }))

          setZeroResultsGeocode(false)

          const errors = { ...formik.errors }

          if (formik.errors.address?.postalCode)
            delete errors.address.postalCode

          if (formik.errors.address?.city) delete errors.address.city

          if (formik.errors.address?.province) delete errors.address.province

          formik.setErrors(errors)
        })
        .catch(error => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              city: '',
              province: '',
              hasSeveralLocalities: false,
              localitiesOptions: null,
            },
          }))

          if (error.code === GEOCODING_ERRORS.zeroResults) {
            setZeroResultsGeocode(true)
          }
        })
    } else {
      setInitialLoad(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPostalCode, formik.values.address.country?.code, fromSearch])

  const getProvincesByCountryCode = async countryCode => {
    try {
      const response = await fetch(provincesFile)
      const arrayBuffer = await response.arrayBuffer()
      const data = new Uint8Array(arrayBuffer)
      const workbook = XLSX.read(data, { type: 'array' })

      const sheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[sheetName]

      const jsonData = XLSX.utils.sheet_to_json(worksheet)

      const filteredData = jsonData.filter(
        entry => entry.Country === countryCode,
      )

      const provinces = filteredData.map(entry => entry.Province)
      return provinces
    } catch (error) {
      console.error('Error al cargar el archivo:', error)
    }
  }

  useEffect(() => {
    if (!firstRender) {
      let countryInfo = countryPhones.find(
        ({ countryCode }) =>
          countryCode.toLocaleLowerCase() ===
          formik?.values?.address?.country?.code.toLocaleLowerCase(),
      )
      if (formik?.values?.address?.phonePrefix !== countryInfo?.dialCode)
        formik.handleChange({
          target: {
            name: 'address.phonePrefix',
            value: countryInfo?.dialCode ?? '+34',
          },
        })

      getProvincesByCountryCode(formik?.values.address.country.code)
        .then(provinces => {
          setProvincesByCountryCode(provinces)
        })
        .catch(error => {
          setProvincesByCountryCode([])
        })
    } else setFirstRender(false)
  }, [formik?.values.address.country])

  const countryPhones = countryTelephoneData.allCountries
    .sort((a, b) => {
      if (a.iso2 === 'es') return -1
      if (b.iso2 === 'es') return 1
      if (a.iso2 === 'pt') return -1
      if (b.iso2 === 'pt') return 1
      return a.name.localeCompare(b.name)
    })
    .map((country, index, arr) => {
      if (
        index === arr.length - 1 ||
        country.dialCode !== arr[index + 1].dialCode
      ) {
        let countryName = countries?.find(
          ({ code }) => code === country.iso2.toLocaleUpperCase(),
        )
        if (countryName)
          return {
            dialCode: `+${country.dialCode}`,
            countryCode: country.iso2,
            name: countryName ? countryName?.label : country?.name,
          }
        if (country.dialCode === '1')
          return {
            dialCode: `+${country.dialCode}`,
            countryCode: 'us',
            name: countryName
              ? countryName?.label
              : 'Estados Unidos de América',
          }
      }
      return null
    })
    .filter(country => country !== null)

  return (
    <form
      onSubmit={formik.handleSubmit}
      onReset={e => {
        formik.handleReset(e)
      }}
    >
      <Box component="section">
        <Box className={classes.fields}>
          <Box className={classes.fieldsRow}>
            <Box className={classes.fieldSM}>
              <InputLabel required>{formatMessage('noun:country')}</InputLabel>
              <Box className={classes.fieldPrefixPhone}>
                <Autocomplete
                  options={countries ?? []}
                  value={formik?.values?.address.country}
                  isOptionEqualToValue={(option, value) =>
                    option.country_code === value.code ||
                    option.code === value.code
                  }
                  disableClearable
                  name="address.country"
                  onChange={(_, country) => {
                    if (country === null) {
                      formik.handleChange({
                        target: {
                          name: 'address.country',
                          value: { code: '', label: '' },
                        },
                      })
                      return
                    }
                    const transformedCountry = {
                      code: country.country_code || country.code,
                      label: country.country_name || country.label,
                    }

                    formik.handleChange({
                      target: {
                        name: 'address.country',
                        value: transformedCountry,
                      },
                    })
                  }}
                  onBlur={e => {
                    formik.validateField(e.target.name)
                  }}
                  getOptionLabel={option =>
                    option.country_name || option.label || ''
                  }
                  className={classes.fieldSM}
                  inputProps={{
                    placeholder: formatMessage('noun:country'),
                    inputProps: { className: 'input-small' },
                    error: formik?.errors?.address?.country,
                    helperText: formik?.errors?.address?.country?.code
                      ? formatMessage(formik?.errors?.address.country.code.key)
                      : null,
                  }}
                />
              </Box>
            </Box>
            <Box className={classes.fieldMD}>
              <GooglePlacesAutocomplete
                placeholder={formatMessage(
                  'shipping-send-form:new-address-agenda:placeholder',
                )}
                label={formatMessage(
                  'shipping-send-form:origin:new-address:address-finder',
                )}
                changeAddress={addressInfo => {
                  setFromSearch(addressInfo?.place)
                  formik.setValues(values => ({
                    ...values,
                    address: {
                      ...values.address,
                      ...addressInfo,
                    },
                  }))
                }}
                countries={countriesToSuggestAddresses}
              />
            </Box>
          </Box>
          <Box className={classes.fieldsRow}>
            <TextField
              name="address.address"
              error={formik?.errors?.address?.address}
              helperText={
                formik?.errors.address?.address
                  ? formatMessage(formik?.errors.address.address.key)
                  : null
              }
              label={formatMessage(
                'shipping-send-form:origin:new-address:address',
              )}
              value={formik?.values.address.address}
              onChange={formik.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldMD}
              InputLabelProps={{ required: true }}
              inputProps={{ readOnly: false, maxLength: 200 }}
            />
            <TextField
              name="address.stair"
              label={formatMessage('noun:stair')}
              value={formik?.values.address.stair}
              onChange={formik.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldXS}
              inputProps={{ readOnly: false }}
            />
            <TextField
              name="address.floor"
              label={formatMessage('noun:floor')}
              value={formik?.values.address.floor}
              onChange={formik.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldXS}
              inputProps={{ readOnly: false }}
            />
            <TextField
              name="address.letter"
              label={formatMessage('noun:letter')}
              value={formik?.values.address.letter}
              onChange={formik.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldXS}
              inputProps={{ readOnly: false }}
            />
          </Box>
          <Box className={classes.fieldsRowGrid}>
            <TextField
              name="address.postalCode"
              error={formik?.errors?.address?.postalCode}
              helperText={
                formik?.errors.address?.postalCode
                  ? formatMessage(
                      formik?.errors.address.postalCode.key ??
                        'shipping-send-form:origin:postal-code:invalid-format',
                    )
                  : null
              }
              label={formatMessage(
                'shipping-send-form:origin:new-address:postal-code',
              )}
              value={formik?.values.address.postalCode}
              onChange={formik?.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldMD}
              InputLabelProps={{ required: true }}
              inputProps={{ readOnly: false, maxLength: 50 }}
              placeholder={
                formik?.values.address.country.code === 'ES'
                  ? formatMessage(
                      'shipping-send-form:origin:new-address:postal-code-es-placeholder',
                    )
                  : formik?.values.address.country.code === 'PT'
                  ? formatMessage(
                      'shipping-send-form:origin:new-address:postal-code-pt-placeholder',
                    )
                  : formatMessage(
                      'shipping-send-form:origin:new-address:postal-code-placeholder',
                    )
              }
            />
            <Box>
              <InputLabel required>
                {formatMessage(
                  'shipping-send-form:origin:new-address:locality',
                )}
              </InputLabel>
              {formik?.values.address.hasSeveralLocalities ? (
                <>
                  <Box className={classes.fieldPrefixPhone}>
                    <Autocomplete
                      options={formik.values.address.localitiesOptions}
                      value={formik.values.address.city}
                      onChange={(event, newValue) => {
                        formik.handleChange({
                          target: { name: 'address.city', value: newValue },
                        })
                      }}
                      className={classes.fieldSM}
                      inputProps={{
                        placeholder: formatMessage(
                          'shipping-send-form:origin:new-address:locality',
                        ),
                        onChange: event => {
                          formik.handleChange({
                            target: {
                              name: 'address.city',
                              value: event.target.value,
                            },
                          })
                        },
                        inputProps: { className: 'input-small' },
                        error: formik?.errors?.address?.city,
                        helperText: formik?.errors?.address?.city
                          ? formatMessage(formik?.errors?.address.city.key)
                          : null,
                      }}
                    />
                  </Box>
                  <FormHelperText className={classes.helperText}>
                    <WarningCircleFillIcon />
                    <Typography variant="label">
                      {formatMessage(
                        'shipping-send-form:helper-text:several-localities-associate-with-postal-code',
                      )}
                    </Typography>
                  </FormHelperText>
                </>
              ) : (
                <Box className={classes.fieldPrefixPhone}>
                  <TextField
                    name="address.city"
                    error={formik.errors?.address?.city}
                    helperText={
                      formik.errors.address?.city
                        ? formatMessage(formik.errors.address.city.key)
                        : null
                    }
                    value={formik?.values.address.city}
                    onChange={e => formik.handleChange(e)}
                    onBlur={e => {
                      formik.validateField(e.target.name)
                    }}
                    className="input-full"
                    InputLabelProps={{ required: true }}
                    inputProps={{
                      maxLength: 50,
                    }}
                    placeholder={formatMessage(
                      'shipping-send-form:origin:new-address:locality',
                    )}
                  />
                </Box>
              )}
            </Box>

            {provicesByCountryCode?.length ? (
              <Autocomplete
                options={provicesByCountryCode ?? []}
                value={formik?.values.address.province}
                isOptionEqualToValue={(option, value) => option === value}
                disableClearable
                name="address.province"
                onChange={(_, province) => {
                  formik.handleChange({
                    target: {
                      name: 'address.province',
                      value: province,
                    },
                  })
                }}
                onBlur={_event => {
                  formik.validateField('address.province')
                }}
                getOptionLabel={option => option || ''}
                inputProps={{
                  required: true,
                  placeholder: formatMessage(
                    'shipping-send-form:origin:new-address:province',
                  ),
                  label: formatMessage(
                    'shipping-send-form:origin:new-address:province',
                  ),
                  error: formik?.errors.address?.province,
                  helperText: formik?.errors.address?.province
                    ? formatMessage(formik?.errors?.address?.province?.key)
                    : null,
                }}
                className={classes.fieldMD}
              />
            ) : (
              <Box>
                <TextField
                  name="address.province"
                  error={formik?.errors?.address?.province}
                  helperText={
                    formik?.errors.address?.province
                      ? formatMessage(formik?.errors.address.province.key)
                      : null
                  }
                  label={formatMessage(
                    'shipping-send-form:origin:new-address:province',
                  )}
                  value={formik?.values.address.province}
                  onChange={formik?.handleChange}
                  onBlur={e => {
                    formik.validateField(e.target.name)
                  }}
                  className="input-full"
                  InputLabelProps={{ required: true }}
                  inputProps={{
                    maxLength: 50,
                  }}
                  placeholder={formatMessage(
                    'shipping-send-form:origin:new-address:province',
                  )}
                />
                {zeroResultsGeocode ? (
                  <FormHelperText className={classes.helperText}>
                    <WarningCircleFillIcon />
                    <Typography variant="label">
                      {formatMessage(
                        'shipping-send-form:helper-text:not-found-localities-input-manually',
                      )}
                    </Typography>
                  </FormHelperText>
                ) : null}
              </Box>
            )}
          </Box>

          <Box className={classes.fieldsRow}>
            <TextField
              name="comments"
              label={formatMessage(
                'shipping-send-form:label:additional-comments',
              )}
              multiline
              maxRows={3}
              onChange={formik?.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldComments}
              value={formik?.values.comments}
              placeholder={formatMessage(
                'shipping-send-form:placeholder:additional-comments',
              )}
            />
          </Box>

          <Typography
            variant="h3"
            className={clsx('secondaryTitle', classes.title)}
          >
            {formatMessage('noun:contact-data')}
          </Typography>
          <Box className={classes.fieldsRow}>
            <TextField
              name="address.name"
              error={formik?.errors?.address?.name}
              helperText={
                formik?.errors.address?.name
                  ? formatMessage(formik?.errors.address.name.key)
                  : null
              }
              label={formatMessage('noun:name-company')}
              value={formik?.values.address.name}
              onChange={formik?.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldMD}
              InputLabelProps={{ required: true }}
              inputProps={{ readOnly: false, maxLength: 50 }}
            />

            <Box className={classes.fieldMD}>
              <InputLabel required={true}>
                {formatMessage('noun:phone')}
                <TooltipButton
                  className={classes.tooltipInput}
                  type="primary"
                  title={formatMessage('tooltip:mobile-phone')}
                  placement="right"
                />
              </InputLabel>
              <Box className={classes.fieldPrefixPhone}>
                {/* NOTE: select con prefijos de teléfono de ejemplo */}
                {/* TODO: cuando se aborde la tarea del prefijo, descomentar el Select. */}
                <Autocomplete
                  noSearchIcon
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue?.length) return options
                    return options.filter(option =>
                      `${option?.dialCode} | ${option?.name}`
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()),
                    )
                  }}
                  options={countryPhones ?? []}
                  value={formik.values?.address.phonePrefix ?? '+34'}
                  isOptionEqualToValue={(option, value) =>
                    option?.dialCode === value
                  }
                  onBlur={e => {
                    formik.validateField('address.phone')
                  }}
                  disableClearable
                  name="address.phonePrefix"
                  onChange={(_, phonePrefix) => {
                    if (phonePrefix === null) {
                      formik?.handleChange({
                        target: {
                          name: 'address.phonePrefix',
                          value: '+34',
                        },
                      })
                      return
                    }

                    formik?.handleChange({
                      target: {
                        name: 'address.phonePrefix',
                        value: phonePrefix?.dialCode,
                      },
                    })
                  }}
                  getOptionLabel={option => {
                    let findCountry = countryPhones.find(
                      ({ dialCode }) => dialCode === option,
                    )
                    if (option?.dialCode) return option?.dialCode
                    return `${findCountry?.dialCode}`
                  }}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      style={{ height: '100%', width: '100%', padding: 15 }}
                      key={`${option?.dialCode} | ${option?.name}`}
                    >
                      {`${option?.dialCode} | ${option?.name}`}
                    </Box>
                  )}
                  sx={classes.fieldPrefixPhoneSelect}
                  className={classes.fieldPrefixPhoneSelect}
                  PaperComponent={props => <StyledPaper {...props} />}
                  inputProps={{
                    placeholder: formatMessage('noun:phonePrefix'),
                  }}
                />
                <TextField
                  name="address.phone"
                  label={null}
                  type="tel"
                  value={formik?.values.address.phone}
                  onChange={formik?.handleChange}
                  onBlur={e => {
                    formik.validateField('address.phone')
                  }}
                  error={formik?.errors?.address?.phone}
                  helperText={
                    formik?.errors.address?.phone
                      ? formatMessage(formik?.errors.address.phone.key)
                      : null
                  }
                  InputLabelProps={{ required: true }}
                  // TODO: cuando se aborde la tarea del prefijo, utilizar la siguiente prop (className).
                  // className={(classes.fieldMD, classes.fieldPrefixPhoneInput)}
                  className={classes.fieldMD}
                  inputProps={{ readOnly: false, maxLength: 15 }}
                />
              </Box>
            </Box>
          </Box>
          <Box className={classes.fieldsRow}>
            <TextField
              name="address.email"
              label={formatMessage('noun:email')}
              type="email"
              value={formik?.values.address.email}
              onChange={formik?.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldMD}
              inputProps={{ readOnly: false }}
            />
            <TextField
              name="address.contactClientCode"
              label={formatMessage('noun:contact-client-code')}
              value={formik?.values.address?.contactClientCode}
              onChange={formik?.handleChange}
              onBlur={e => {
                formik.validateField(e.target.name)
              }}
              className={classes.fieldMD}
              inputProps={{ readOnly: false, maxLength: 50 }}
            />
          </Box>
        </Box>
      </Box>
      <Box className={classes.actionButtons}>
        <Button
          type="button"
          size="small"
          onClick={onCancel}
          variant="outlined"
        >
          {formatMessage('action:cancel')}
        </Button>
        <Button type="submit" size="small">
          {formatMessage('action:save')}
        </Button>
      </Box>
    </form>
  )
}

AddEditDirection.propTypes = propTypes

export default AddEditDirection
